export const DEVICE_TYPES = {
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  MOBILE: 'mobile',
  UNKNOWN: 'unknown',
};

const OS_TYPES = {
  ANDROID: 'android',
  CHROME_OS: 'chromeos',
  FIRE: 'fire',
  IOS: 'ios',
  LINUX: 'linux',
  MACOS: 'macos',
  WINDOWS: 'windows',
  UNKNOWN: 'unknown',
};

const KINDLE_DEVICES = /KF[A-Z0-9]+|KFTT|KFOT|SD4930UR|Kindle|Silk|Fire(?:\s+HD|\s+Tablet)?/i;
const WINDOWS_MOBILE = /Windows Phone|IEMobile|Windows CE|Windows Mobile|WCE/i;
const MOBILE_UA =
  /Android.*Mobile|iPhone|iPod|BlackBerry|\bBB10\b|Opera Mini|Opera Mobi|Mobile Safari|CriOS.*Mobile|FxiOS.*Mobile/i;
const TABLET_UA =
  /iPad|Tablet|PlayBook|Nexus (?:7|9|10)|Pixel C|SM-T|GT-P|SCH-I800|Kindle|Silk|KF[A-Z0-9]+|Hudl|Lenovo Tab|Mi Pad|PadFone|Transformer Pad/i;

const getNavigator = () => {
  return typeof window !== 'undefined' && window.navigator ? window.navigator : {};
};

const getUserAgent = () => {
  const navigator = getNavigator();
  return navigator.userAgent || '';
};

const getUserAgentPlatform = () => {
  const navigator = getNavigator();
  const userAgentDataPlatform = navigator.userAgentData && navigator.userAgentData.platform;
  return userAgentDataPlatform || navigator.platform || '';
};

const getMaxTouchPoints = () => {
  const navigator = getNavigator();
  return navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0;
};

const getScreenSize = () => {
  if (typeof window === 'undefined' || !window.screen) {
    return { shortestSide: 0, longestSide: 0 };
  }

  const width = window.screen.width || window.innerWidth || 0;
  const height = window.screen.height || window.innerHeight || 0;

  return {
    shortestSide: Math.min(width, height),
    longestSide: Math.max(width, height),
  };
};

const isIPadOSDesktopMode = () => {
  const platform = getUserAgentPlatform();
  return /Mac/i.test(platform) && getMaxTouchPoints() > 1;
};

const isIOSPlatform = (platform) => {
  return /iPhone|iPod|iPad/i.test(platform);
};

const isLikelyTabletByScreen = () => {
  const { shortestSide, longestSide } = getScreenSize();

  return getMaxTouchPoints() > 1 && shortestSide >= 600 && longestSide <= 1400;
};

const detectOs = (ua, platform) => {
  if (isIPadOSDesktopMode() || isIOSPlatform(platform) || /iPhone|iPod|iPad/i.test(ua)) {
    return OS_TYPES.IOS;
  }

  if (KINDLE_DEVICES.test(ua)) {
    return OS_TYPES.FIRE;
  }

  if (/Android/i.test(ua)) {
    return OS_TYPES.ANDROID;
  }

  if (WINDOWS_MOBILE.test(ua) || /Windows/i.test(platform) || /Windows/i.test(ua)) {
    return OS_TYPES.WINDOWS;
  }

  if (/CrOS/i.test(ua) || /Chrome OS/i.test(platform)) {
    return OS_TYPES.CHROME_OS;
  }

  if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) {
    return OS_TYPES.MACOS;
  }

  if (/Linux/i.test(platform) || /Linux/i.test(ua)) {
    return OS_TYPES.LINUX;
  }

  return OS_TYPES.UNKNOWN;
};

const getAndroidVersion = (ua) => {
  const android = ua.match(/Android\s+([\d.]+)/i);
  return android && android[1] ? android[1] : 'unknown';
};

const getIosVersion = (ua) => {
  const navigator = getNavigator();
  const versionSource = `${ua} ${navigator.appVersion || ''}`;
  const ios = versionSource.match(/(?:CPU (?:iPhone )?OS|iPhone OS|OS)\s+([\d_]+)/i);
  if (!ios || !ios[1]) {
    return 'unknown';
  }

  return ios[1].replace(/_/g, '.');
};

const getMajorVersion = (version) => {
  const major = parseInt(version, 10);
  return isNaN(major) ? 0 : major;
};

const detectDeviceType = (ua, os) => {
  const navigator = getNavigator();
  const uaDataMobile = navigator.userAgentData && navigator.userAgentData.mobile === true;
  const platform = getUserAgentPlatform();

  if (isIPadOSDesktopMode() || /iPad/i.test(platform)) {
    return DEVICE_TYPES.TABLET;
  }

  if (/iPhone|iPod/i.test(platform) || WINDOWS_MOBILE.test(ua) || MOBILE_UA.test(ua) || uaDataMobile) {
    return DEVICE_TYPES.MOBILE;
  }

  if (KINDLE_DEVICES.test(ua) || TABLET_UA.test(ua)) {
    return DEVICE_TYPES.TABLET;
  }

  if (os === OS_TYPES.ANDROID) {
    return /Mobile/i.test(ua) ? DEVICE_TYPES.MOBILE : DEVICE_TYPES.TABLET;
  }

  if (isLikelyTabletByScreen() && os !== OS_TYPES.WINDOWS && os !== OS_TYPES.MACOS) {
    return DEVICE_TYPES.TABLET;
  }

  if (os !== OS_TYPES.UNKNOWN) {
    return DEVICE_TYPES.DESKTOP;
  }

  return DEVICE_TYPES.UNKNOWN;
};

const getFamily = (os) => {
  if (os === OS_TYPES.ANDROID || os === OS_TYPES.FIRE) {
    return OS_TYPES.ANDROID;
  }

  if (os === OS_TYPES.IOS) {
    return OS_TYPES.IOS;
  }

  if (os === OS_TYPES.WINDOWS) {
    return OS_TYPES.WINDOWS;
  }

  return os;
};

function detectPlatform() {
  const ua = getUserAgent();
  const platform = getUserAgentPlatform();
  const os = detectOs(ua, platform);
  const version = os === OS_TYPES.ANDROID ? getAndroidVersion(ua) : os === OS_TYPES.IOS ? getIosVersion(ua) : 'unknown';
  const majorVersion = getMajorVersion(version);
  const type = detectDeviceType(ua, os);

  return {
    type,
    os,
    family: getFamily(os),
    version,
    majorVersion,
    versionKnown: version !== 'unknown',
    isIOS18OrLater: os === OS_TYPES.IOS && majorVersion > 0 ? majorVersion >= 18 : null,
    isTouch: getMaxTouchPoints() > 0 || (typeof window !== 'undefined' && 'ontouchstart' in window),
  };
}

/**
 * Get estimated platform the site is running on.
 * Uses UA Client Hints when available, then modern user-agent and touch/screen fallbacks.
 *
 * iOS/iPadOS can withhold its version. In that case `version` is "unknown" and
 * `isIOS18OrLater` is null rather than an incorrect false result.
 *
 * @returns {{ type: string, os: string, family: string, version: string, majorVersion: number, versionKnown: boolean, isIOS18OrLater: boolean|null, isTouch: boolean }}
 */
export const getPlatform = () => {
  return detectPlatform();
};

export const getDeviceType = () => {
  return getPlatform().type;
};

export const isMobile = () => {
  return getDeviceType() === DEVICE_TYPES.MOBILE;
};

export const isTablet = () => {
  return getDeviceType() === DEVICE_TYPES.TABLET;
};

export const isDesktop = () => {
  return getDeviceType() === DEVICE_TYPES.DESKTOP;
};

export const isIOS = () => {
  return getPlatform().os === OS_TYPES.IOS;
};

export const isIOS18OrLater = () => {
  return getPlatform().isIOS18OrLater;
};
